import { Box, Divider } from '@mui/material'
import { useFrontContext } from '@/hooks/useFrontContext'
import { useLoadNumber } from '@/hooks/useLoadNumber'
import { useLoadDetails } from '@/hooks/useLoadDetails'
import { useAuth } from '@/hooks/useAuth'
import { getOrderedRouteStops } from '@/utils/dispatchUtils'

import Loading from '@/components/common/Loading'
import ErrorState from '@/components/common/ErrorState'
import EmptyState from '@/components/common/EmptyState'
import QuickLinks from '@/components/common/QuickLinks'
import LoadSummary from './LoadSummary'
import StopsList from './StopsList'
import DispatchDetails from './DispatchDetails'
import TrackingStatus from './TrackingStatus'

const LoadDetailsContainer = () => {
  // Get auth context for permission check
  const { canReadLoad, b18User, loading: authLoading } = useAuth()

  // Get conversation from Front context
  const {
    conversation,
    loading: frontLoading,
    error: frontError
  } = useFrontContext()

  // Detect load number from conversation (includes linked via B18 check)
  const {
    loadNumber,
    matchType,
    loading: loadNumberLoading,
    linkedLoadId
  } = useLoadNumber(conversation)

  // Fetch load details from B18 API
  const {
    load,
    loading: loadLoading,
    error: loadError,
    refetch
  } = useLoadDetails({ loadNumber, loadId: linkedLoadId })

  // Loading state (include auth loading)
  if (authLoading || frontLoading || loadNumberLoading || loadLoading) {
    return <Loading message="Loading load details..." />
  }

  // Permission check - user doesn't have access to loads
  if (b18User && !canReadLoad) {
    return (
      <ErrorState message="You do not have permission to view this load in B18." />
    )
  }

  // Front SDK error
  if (frontError) {
    return (
      <ErrorState
        message="Failed to connect to Front"
        onRetry={() => window.location.reload()}
      />
    )
  }

  // No conversation selected
  if (!conversation) {
    return (
      <EmptyState
        title="No Conversation"
        message="Select a conversation to view load details"
      />
    )
  }

  // No load number detected (and not linked via B18)
  if ((!loadNumber && !linkedLoadId) || matchType === 'none') {
    return (
      <EmptyState
        title="No Load Found"
        message="No load number detected in this conversation's tags or subject line"
      />
    )
  }

  // API error
  if (loadError) {
    // Carrier-scope-filter rejection (P2-1157) — restricted dispatcher
    // hit a load outside their include/exclude scope. Show a friendly
    // message instead of raw "Request failed with status code 403".
    const status = (loadError as unknown as { response?: { status?: number } })
      .response?.status
    const friendlyMessage =
      status === 403
        ? 'You do not have access to this load. Contact your administrator if you believe this is a mistake.'
        : loadError.message ||
          `Failed to load details for Load #${loadNumber}`
    return <ErrorState message={friendlyMessage} onRetry={refetch} />
  }

  // No load data
  if (!load) {
    return (
      <EmptyState
        title="Load Not Found"
        message={`Load #${loadNumber} was not found in Bridge18`}
      />
    )
  }

  // Build the route stop list in dispatch order, each empty move bundled into
  // its own segment (mirrors the web's expanded dispatch rows — P2-1315).
  const allStops = getOrderedRouteStops(load)

  // Render load details
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        bgcolor: 'grey.50'
      }}
    >
      {/* Scrollable content */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          bgcolor: 'background.paper'
        }}
      >
        <LoadSummary load={load} />
        <Divider />
        <TrackingStatus load={load} />
        <Divider />
        <StopsList stops={allStops} packages={load.packages} />
        <Divider />
        <DispatchDetails load={load} />
      </Box>

      {/* Fixed bottom button */}
      <Box
        sx={{
          p: 2,
          borderTop: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper'
        }}
      >
        <QuickLinks loadId={load._id} />
      </Box>
    </Box>
  )
}

export default LoadDetailsContainer
